import React, { useEffect, useState, useCallback, useMemo } from "react";
import { format } from "date-fns";
import {
    fetchCurrencies,
    fetchUtilAccounts,
    fetchNamedUsers,
    fetchTransactionTypes,
    fetchStores,
    fetchTransactionmap,
    API_BASE_PATH,
    API_ROOT_PATH,
} from "../apiHelper";
import FastAPIClient from "../services/apiClient";
import { useToast } from "../hooks/useToast";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import SearchableSelectBox from "../components/ui/SearchableSelectBox";
import DropZone from "../components/ui/DropZone";
import DateTimeInput from "../components/ui/DateTimeInput";
import { Plus, Trash2, Loader2, Info, XCircle, ReceiptText, Save } from 'lucide-react';
import { useNavigate } from "react-router-dom";

const client = new FastAPIClient();

const NewJournalEntry = () => {
    const { showToast } = useToast();
    const navigate = useNavigate();

    const [loadingData, setLoadingData] = useState(true);
    const [pageError, setPageError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [user, setUser] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [storeOptions, setStoreOptions] = useState([]);   // <-- NEW

    const [postings, setPostings] = useState([
        { account_id: "", amount: "", posting_type: "debit" },
        { account_id: "", amount: "", posting_type: "credit" },
    ]);

    const [form, setForm] = useState({
        ref: "",
        organization_id: "",
        creator_id: "",
        customer_id: "",
        store_id: "",
        currency_code: "NGN",
        transaction_date: format(new Date(), 'yyyy-MM-ddTHH:mm'),
        description: "",
        transaction_type: "",
        attachmenturl: '',
        mimetype: '',
        mimetypeencoding: '',
        filename: '',
        filesize: 0,
    });

    // --------------------------------------------------------------------
    // Load transaction mapping when transaction_type changes
    // --------------------------------------------------------------------
    const loadTransactionMap = useCallback(async (transactionType) => {
        if (!transactionType || !user?.users_id) return;

        try {
            const mapping = await fetchTransactionmap({
                users_id: user.users_id,
                transaction_type: transactionType,
            });


            if (mapping?.debit_account_id && mapping?.credit_account_id) {
                // Update first two postings
                setPostings(prev => {
                    const updated = [...prev];
                    // Debit line (index 0)
                    updated[0] = {
                        ...updated[0],
                        account_id: mapping.debit_account_id,
                        posting_type: "debit"
                    };
                    // Credit line (index 1)
                    updated[1] = {
                        ...updated[1],
                        account_id: mapping.credit_account_id,
                        posting_type: "credit"
                    };
                    return updated;
                });
            }
        } catch (err) {
            console.warn("No transaction map found or failed to load:", err);
            // Optional: showToast("No default accounts for this transaction type.", "info");
        }
    }, [user?.users_id]);

    // Trigger when transaction_type changes
    useEffect(() => {
        if (form.transaction_type) {
            // console.log(form.transaction_type)
            loadTransactionMap(form.transaction_type);
        } else {
            // Reset postings if transaction type is cleared
            setPostings([
                { account_id: "", amount: "", posting_type: "debit" },
                { account_id: "", amount: "", posting_type: "credit" },
            ]);
        }
    }, [form.transaction_type, loadTransactionMap]);

    // --------------------------------------------------------------------
    // Load all required data (including stores)
    // --------------------------------------------------------------------
    const loadAllData = useCallback(async (loggedInUser) => {
        if (!loggedInUser) return;
        setLoadingData(true);
        setPageError(null);
        try {
            const [
                fetchedUsers,
                fetchedAccounts,
                fetchedCurrencies,
                fetchedTransactionTypes,
                fetchedStores                     // <-- NEW
            ] = await Promise.all([
                fetchNamedUsers(loggedInUser),
                fetchUtilAccounts(loggedInUser),
                fetchCurrencies(),
                fetchTransactionTypes(),
                fetchStores(loggedInUser)          // <-- pass user
            ]);

            setCustomers(fetchedUsers.map(u => ({
                label: `${u.user?.first_name || ''} ${u.user?.last_name || ''} (${u.user?.credential?.logon_id || 'N/A'})`,
                value: u.member_id,
            })));

            setAccounts(fetchedAccounts.map(account => ({
                label: `${account.account_code} - ${account.name}`,
                value: account.account_id,
                account_type_label: account.account_type?.label,
            })));

            setCurrencies(fetchedCurrencies.map(currency => ({
                label: `${currency.setccurr} - ${currency.setcnote}`,
                value: currency.setccurr,
            })));

            setTransactionTypes(fetchedTransactionTypes.map(type => ({
                label: type.label,
                value: type.transaction_type,
            })));

            // ---- Stores ----
            setStoreOptions(fetchedStores.map(store => ({
                label: store.identifier,
                value: store.store_id,
                address: store.address
            })));

        } catch (err) {
            console.error("Error loading journal entry data:", err);
            const errorMessage = err.response?.data?.detail || "Failed to load necessary data. Please try again.";
            setPageError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setLoadingData(false);
        }
    }, [showToast]);

    // --------------------------------------------------------------------
    // Load user from localStorage
    // --------------------------------------------------------------------
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to create journal entries.", "info");
            navigate('/login');
        } else {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
            // console.log(parsedUser)
            setForm(prev => ({
                ...prev,
                creator_id: parsedUser.users_id,
                organization_id: parsedUser.organization_id
            }));
        }
    }, [showToast, navigate]);

    // --------------------------------------------------------------------
    // Trigger data load when user becomes available
    // --------------------------------------------------------------------
    useEffect(() => {
        if (user) {
            loadAllData(user);
        }
    }, [user, loadAllData]);

    // --------------------------------------------------------------------
    // Generic input handler
    // --------------------------------------------------------------------
    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    }, []);

    // --------------------------------------------------------------------
    // Posting handlers (unchanged)
    // --------------------------------------------------------------------
    const handlePostingChange = useCallback((index, field, value) => {
        const updatedPostings = [...postings];
        updatedPostings[index][field] = value;
        setPostings(updatedPostings);
    }, [postings]);

    const addPosting = useCallback(() => {
        setPostings([...postings, { account_id: "", amount: "", posting_type: "debit" }]);
    }, [postings]);

    const removePosting = useCallback((index) => {
        const updatedPostings = [...postings];
        updatedPostings.splice(index, 1);
        setPostings(updatedPostings);
    }, [postings]);

    const debitTotal = useMemo(() =>
        postings
            .filter((p) => p.posting_type === "debit")
            .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
        [postings]
    );

    const creditTotal = useMemo(() =>
        postings
            .filter((p) => p.posting_type === "credit")
            .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
        [postings]
    );

    const isBalanced = useMemo(() => debitTotal.toFixed(2) === creditTotal.toFixed(2), [debitTotal, creditTotal]);

    // --------------------------------------------------------------------
    // Attachment handling (unchanged)
    // --------------------------------------------------------------------
    const handleImageUploadComplete = useCallback((uploadedFiles) => {
        if (uploadedFiles && uploadedFiles.length > 0) {
            const fileData = uploadedFiles[0];
            setForm(prev => ({
                ...prev,
                attachmenturl: fileData.url,
                filename: fileData.filename,
                mimetype: fileData.mimetype,
                mimetypeencoding: fileData.encoding,
                filesize: fileData.size,
            }));
            showToast("Receipt image uploaded successfully!", "success");
        }
    }, [showToast]);

    // --------------------------------------------------------------------
    // Submit handler – now includes store_id
    // --------------------------------------------------------------------
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setPageError(null);

        if (!isBalanced) {
            showToast("Debits and credits must be equal.", "error");
            setIsSubmitting(false);
            return;
        }
        if (postings.length === 0) {
            showToast("At least one posting is required.", "error");
            setIsSubmitting(false);
            return;
        }
        if (postings.some(p => !p.account_id || !p.amount || parseFloat(p.amount) <= 0)) {
            showToast("All postings must have a selected account and a positive amount.", "error");
            setIsSubmitting(false);
            return;
        }
        if (!form.ref || !form.transaction_date || !form.currency_code || !form.transaction_type) {
            showToast("Reference, Transaction Date, Currency, and Transaction Type are required.", "error");
            setIsSubmitting(false);
            return;
        }

        const formattedTransactionDate = form.transaction_date
            ? new Date(form.transaction_date).toISOString()
            : null;

        const payload = {
            ...form,
            transaction_date: formattedTransactionDate,
            store_id: form.store_id === "" ? null : parseInt(form.store_id, 10), // <-- NEW
            postings: postings.map(p => ({
                ...p,
                amount: parseFloat(p.amount)
            })),
            customer_id: form.customer_id === "" ? null : form.customer_id,
            attachmenturl: form.attachmenturl === "" ? null : form.attachmenturl,
            filename: form.filename === "" ? null : form.filename,
            mimetype: form.mimetype === "" ? null : form.mimetype,
            mimetypeencoding: form.mimetypeencoding === "" ? null : form.mimetypeencoding,
            filesize: form.filesize === 0 ? null : form.filesize,
        };

        try {
            // console.log("Submitting Journal Entry Payload:", payload);
            const response = await client.postData(`${API_BASE_PATH}/accounting/create-journal-entry`, payload);
            showToast(response.msg || "Journal Entry created successfully!", 'success');

            // 

            setForm(prev => ({
                ...prev,
                ref: "",
                customer_id: "",
                store_id: "",
                currency_code: "NGN",
                transaction_date: format(new Date(), 'yyyy-MM-ddTHH:mm'),
                description: "",
                transaction_type: "",
                attachmenturl: "",
                mimetype: "",
                mimetypeencoding: "",
                filename: "",
                filesize: 0,
            }));

            setPostings([
                { account_id: "", amount: "", posting_type: "debit" },
                { account_id: "", amount: "", posting_type: "credit" },
            ]);

        } catch (err) {
            console.error("Error creating journal entry:", err);
            const errorMessage = err.response?.data?.detail || `Failed to create journal entry: ${err.message || err}`;
            setPageError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --------------------------------------------------------------------
    // UI – Loading / Errors / No-User
    // --------------------------------------------------------------------
    if (loadingData) {
        return (
            <div className='flex items-center justify-center min-h-[60vh] text-center p-8 text-text-secondary'>
                <Loader2 size={32} className="animate-spin text-gmail-blue" />
                <p className="ml-3 text-lg">Loading data for journal entry...</p>
            </div>
        );
    }

    if (pageError) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-gmail-red'>
                <XCircle size={32} className="mb-2" />
                <p className="text-lg mb-4">Error: {pageError}</p>
                <Button onClick={() => { if (user) loadAllData(user); }} variant="primary">
                    Retry Loading Data
                </Button>
            </div>
        );
    }

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to create journal entries.</p>
            </div>
        );
    }

    // --------------------------------------------------------------------
    // Main Form
    // --------------------------------------------------------------------
    return (
        <div className="flex flex-1 justify-center items-start py-10 px-4 sm:px-6 lg:px-8 bg-gray-100 min-h-screen">
            <div className="w-full bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-3xl font-bold text-text-primary mb-8 text-center flex items-center justify-center gap-3">
                    <ReceiptText size={32} className="text-gmail-blue" /> Record New Journal Entry
                </h2>
                <p className="text-text-secondary text-center mb-8 max-w-md mx-auto">
                    Manually record a financial transaction that affects two or more accounts, ensuring debits equal credits.
                </p>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 h-full">
                    {/* ---------- LEFT COLUMN – Transaction Details ---------- */}
                    <div className="flex flex-col space-y-6 h-full">
                        <Card className="shadow border-gray-200 flex-1">
                            <div className="p-6 space-y-4">
                                <h3 className="text-lg font-semibold text-text-primary">Transaction Details</h3>

                                <Input
                                    name="ref"
                                    value={form?.ref || ""}
                                    onChange={handleInputChange}
                                    label="Reference Number"
                                    type="text"
                                    id="ref"
                                    placeholder="e.g., INV-2023-001"
                                    required
                                />

                                <DateTimeInput
                                    name="transaction_date"
                                    value={form?.transaction_date || ""}
                                    onChange={handleInputChange}
                                    label="Transaction Date & Time"
                                    id="transaction_date"
                                    placeholder="Select transaction date and time"
                                    required
                                />

                                <SearchableSelectBox
                                    name="transaction_type"
                                    label="Transaction Type"
                                    value={form?.transaction_type || ""}
                                    onChange={handleInputChange}
                                    options={transactionTypes}
                                    placeholder="Select a transaction type"
                                    required
                                />

                                {/* ---------- NEW: Store Select ---------- */}
                                <SearchableSelectBox
                                    name="store_id"
                                    label="Store (Optional)"
                                    value={form?.store_id || ""}
                                    onChange={handleInputChange}
                                    options={storeOptions}
                                    placeholder="Select a store"
                                />

                                <SearchableSelectBox
                                    name="customer_id"
                                    label="Customer (Optional)"
                                    value={form?.customer_id || ""}
                                    onChange={(e) => handleInputChange({ target: { name: "customer_id", value: parseInt(e.target.value, 10) || "" } })}
                                    options={customers}
                                    placeholder="Select a customer"
                                />

                                <Select
                                    name="currency_code"
                                    label="Transaction Currency"
                                    value={form?.currency_code || ""}
                                    onChange={handleInputChange}
                                    options={currencies}
                                    required
                                />

                                <Input
                                    name="description"
                                    value={form?.description || ""}
                                    onChange={handleInputChange}
                                    required
                                    label="Comments (Optional)"
                                    type="textarea"
                                    id="description"
                                    placeholder="Enter comments or brief description"
                                />
                            </div>
                        </Card>
                    </div>

                    {/* ---------- RIGHT COLUMN – Receipt Upload ---------- */}
                    <div className="flex flex-col space-y-6 h-full">
                        <Card className="shadow border-gray-200 flex-1 overflow-y-auto custom-scrollbar">
                            <div className="p-6 space-y-4">
                                <h3 className="text-lg font-semibold text-text-primary mb-4">Attach Receipt (Optional)</h3>
                                {form.attachmenturl && (
                                    <div className="flex flex-col items-center justify-center gap-2 text-sm text-text-secondary mb-4">
                                        {form.mimetype && form.mimetype.startsWith('image/') ? (
                                            <img
                                                src={`${API_ROOT_PATH}${form.attachmenturl}`}
                                                alt="Receipt Preview"
                                                className="max-w-full h-auto rounded-lg shadow-md"
                                                style={{ maxHeight: '200px' }}
                                            />
                                        ) : (
                                            <ReceiptText size={64} className="text-gray-400 mb-2" />
                                        )}
                                        <span>Uploaded: <a href={`${API_ROOT_PATH}${form.attachmenturl}`} target="_blank" rel="noopener noreferrer" className="text-gmail-blue hover:underline">
                                            {form.filename || 'View Attachment'}
                                        </a></span>
                                        <Button
                                            onClick={() => setForm(prev => ({ ...prev, attachmenturl: '', filename: '', mimetype: '', mimetypeencoding: '', filesize: 0 }))}
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 hover:bg-red-100 mt-2"
                                        >
                                            <Trash2 size={16} className="mr-1" /> Remove Attachment
                                        </Button>
                                    </div>
                                )}
                                <DropZone
                                    label="Drag and drop receipt image here or click to upload (JPG, PNG, PDF)"
                                    accept=".jpg,.png,.jpeg,.pdf"
                                    uploadUrl={`${API_BASE_PATH}/uploads/upload`}
                                    onUploadComplete={handleImageUploadComplete}
                                />
                            </div>
                        </Card>
                    </div>

                    {/* ---------- JOURNAL POSTINGS (spans both columns) ---------- */}
                    <div className="md:col-span-2 flex flex-col space-y-6">
                        <Card className="shadow border-gray-200 flex-1 overflow-y-auto custom-scrollbar">
                            <div className="p-6 space-y-4">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold text-text-primary">Journal Postings</h3>
                                    <Button onClick={addPosting} variant="secondary" size="sm" className="flex items-center gap-1">
                                        <Plus size={16} /> Add Line
                                    </Button>
                                </div>
                                {postings.map((p, idx) => (
                                    <div key={idx} className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-center">
                                        <div className="sm:col-span-2">
                                            <SearchableSelectBox
                                                name="account_id"
                                                value={p.account_id}
                                                onChange={(e) => handlePostingChange(idx, "account_id", parseInt(e.target.value, 10) || "")}
                                                options={accounts}
                                                placeholder="Select Account"
                                            />
                                        </div>
                                        <Select
                                            name="posting_type"
                                            value={p.posting_type}
                                            onChange={(e) => handlePostingChange(idx, "posting_type", e.target.value)}
                                            options={[
                                                { label: "Debit", value: "debit" },
                                                { label: "Credit", value: "credit" },
                                            ]}
                                        />
                                        <Input
                                            type="number"
                                            name="amount"
                                            placeholder="Amount"
                                            value={p.amount}
                                            onChange={(e) => handlePostingChange(idx, "amount", e.target.value)}
                                            required
                                        />
                                        <Button onClick={() => removePosting(idx)} variant="ghost" size="sm" className="p-1.5 text-red-500 hover:bg-red-100">
                                            <Trash2 size={18} />
                                        </Button>
                                    </div>
                                ))}
                                <div className="flex justify-between text-base font-semibold pt-4 border-t border-gray-200 mt-4">
                                    <span>Total Debit: <span className="text-gmail-blue">{form.currency_code} {debitTotal.toFixed(2)}</span></span>
                                    <span>Total Credit: <span className="text-gmail-green">{form.currency_code} {creditTotal.toFixed(2)}</span></span>
                                </div>

                                {!isBalanced && (
                                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                                        <Info size={16} /> Debits and credits must be equal before submission.
                                    </div>
                                )}
                            </div>
                        </Card>
                    </div>

                    {/* ---------- SUBMIT BUTTON ---------- */}
                    <div className="md:col-span-2 flex justify-end mt-6">
                        <Button
                            type="submit"
                            variant="primary"
                            className="flex items-center gap-2 px-6 py-3 text-lg"
                            disabled={isSubmitting || !isBalanced || postings.length === 0 || postings.some(p => !p.account_id || !p.amount || parseFloat(p.amount) <= 0)}
                        >
                            {isSubmitting ? (
                                <Loader2 className="animate-spin h-5 w-5" />
                            ) : (
                                <Save size={20} />
                            )}
                            {isSubmitting ? 'Saving Entry...' : 'Submit Journal Entry'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewJournalEntry;